import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { newUuid, requestFile, saveFile, useWindow } from '@krp/client-runtime'
import {
  DUPLICATES_URLS,
  DuplicatesScreen,
  FILTER_PARAM,
  PAGE_PARAM,
  SCAN_POLL_MS,
  buildDuplicatesVm,
  buildScanVm,
  cardToggled,
  duplicateDecide,
  duplicatePairs,
  duplicateScan,
  duplicateScanResume,
  duplicateScanStart,
  duplicateScanStop,
  isActive,
  pairResolved,
  parseFilter,
  parsePage,
  selectionCleared,
  shownError,
  withView,
  type DecisionKind,
  type DuplicatePair,
  type DuplicatesActions,
  type ExportView,
  type PairFilter,
  type ScanActions,
  type ShownError,
} from '@krp/duplicates-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'

const IDLE_EXPORT: ExportView = { phase: 'idle', fileName: null, error: null }

/**
 * Binding «Дубли» одного источника (RP-DUP8). Данные сервера — только в кэше запросов; здесь же намерения человека, которым
 * не место в пакете: фильтр и страница — в адресе окна (`?f=`, `?p=`), выбранная карточка — в слайсе, ход команд и отказ у
 * пары — в состоянии экрана. Ключ операции — на намерение человека: повтор после обрыва ответа несёт тот же ключ, и служба
 * вернёт исполненный итог, а не выполнит решение второй раз (Р-1).
 */
export function DuplicatesSourceBinding() {
  const { params } = useWindow()
  const resourceId = params.rid ?? ''
  const dispatch = useAppDispatch()
  const selected = useAppSelector((state) => state.duplicates.selection.selected)
  const [search, setSearch] = useSearchParams()
  const filter = parseFilter(search.get(FILTER_PARAM))
  const page = parsePage(search.get(PAGE_PARAM))

  // Проверка идёт на сервере: пока она занимает источник, состояние и список пар перечитываются раз в 2 с.
  const [pollMs, setPollMs] = useState(0)
  const scanResult = duplicateScan.useQuery({ resourceId }, { pollingInterval: pollMs })
  const scan = scanResult.data?.scan ?? null
  const active = isActive(scan)
  const wantedPoll = active ? SCAN_POLL_MS : 0
  if (wantedPoll !== pollMs) setPollMs(wantedPoll)

  const view = useMemo(() => ({ resourceId, filter, page }), [resourceId, filter, page])
  const pairsResult = duplicatePairs.useQuery(view, { pollingInterval: pollMs })
  const refetchPairs = pairsResult.refetch
  // Проверка закончилась (завершена, остановлена, упала): последние найденные пары приходят одним перечитыванием.
  const wasActive = useRef(false)
  useEffect(() => {
    if (wasActive.current && !active) void refetchPairs()
    wasActive.current = active
  }, [active, refetchPairs])

  const setView = (nextFilter: PairFilter, nextPage: number) => {
    setSearch((current) => withView(current, nextFilter, nextPage), { replace: true })
  }

  // Команды над парами.
  const [decide] = duplicateDecide.useMutation()
  const [busyPairs, setBusyPairs] = useState<ReadonlySet<number>>(() => new Set())

  // Страница за последней (разобрали пары, список стал короче): адрес приводится к тому, что подтвердил сервер. Пока команды идут
  // или список перечитывается, пустая страница может быть догадкой, и адрес не трогается.
  const pairsData = pairsResult.currentData
  const settled = busyPairs.size === 0 && !pairsResult.isFetching
  useEffect(() => {
    if (!settled || pairsData === undefined || pairsData.items.length > 0 || page <= 1) return
    const pages = Math.max(1, Math.ceil(pairsData.total / pairsData.pageSize))
    setSearch((current) => withView(current, filter, Math.min(page - 1, pages)), { replace: true })
  }, [settled, pairsData, page, filter, setSearch])
  const [pairErrors, setPairErrors] = useState<ReadonlyMap<number, ShownError>>(() => new Map())
  const operations = useRef(new Map<string, string>())

  const operationFor = (key: string): string => {
    const known = operations.current.get(key)
    if (known !== undefined) return known
    const created = newUuid()
    operations.current.set(key, created)
    return created
  }

  const decideOn = (pair: DuplicatePair, kind: DecisionKind, key: string | null) => {
    const intent = `${resourceId}:${pair.id}:${kind}:${key ?? ''}`
    setBusyPairs((current) => new Set(current).add(pair.id))
    setPairErrors((current) => {
      const next = new Map(current)
      next.delete(pair.id)
      return next
    })
    void decide({ resourceId, pairId: pair.id, kind, key, operationId: operationFor(intent), view })
      .unwrap()
      .then(() => {
        operations.current.delete(intent)
        dispatch(pairResolved(pair.id))
      })
      .catch((error: unknown) => {
        setPairErrors((current) => new Map(current).set(pair.id, shownError(`Не удалось применить решение к паре № ${pair.id}`, error)))
      })
      .finally(() => {
        setBusyPairs((current) => {
          const next = new Set(current)
          next.delete(pair.id)
          return next
        })
      })
  }

  // Выгрузка: файл идёт мимо кэша запросов, в Redux его нет.
  const [exportView, setExportView] = useState<ExportView>(IDLE_EXPORT)

  const listActions: DuplicatesActions = {
    filterChanged: (next) => {
      dispatch(selectionCleared())
      setView(next, 1)
    },
    pageChanged: (next) => {
      dispatch(selectionCleared())
      setView(filter, next)
    },
    cardSelected: (pairId, key) => dispatch(cardToggled({ resourceId, pairId, key })),
    markCopy: (pair, key) => decideOn(pair, 'copy', key),
    notDuplicate: (pair) => decideOn(pair, 'not-duplicate', null),
    reset: (pair) => decideOn(pair, 'reset', null),
    reload: () => void refetchPairs(),
    exportFile: (format, scope) => {
      setExportView({ phase: 'preparing', fileName: null, error: null })
      void dispatch(requestFile({ url: DUPLICATES_URLS.export(resourceId), params: { format, scope } })).then((result) => {
        if (!result.ok) {
          setExportView({ phase: 'failed', fileName: null, error: shownError('Не удалось подготовить файл', result.error) })
          return
        }
        saveFile(result.data, `Дубли.${format}`)
        setExportView({ phase: 'saved', fileName: result.data.fileName, error: null })
      })
    },
  }

  // Команды над проверкой.
  const [startScan] = duplicateScanStart.useMutation()
  const [stopScan] = duplicateScanStop.useMutation()
  const [resumeScan] = duplicateScanResume.useMutation()
  const [scanBusy, setScanBusy] = useState(false)
  const [scanError, setScanError] = useState<ShownError | null>(null)
  const refetchScan = scanResult.refetch

  const runScanCommand = (intent: string, send: (operationId: string) => Promise<unknown>) => {
    setScanBusy(true)
    setScanError(null)
    void send(operationFor(intent))
      .then(() => {
        operations.current.delete(intent)
      })
      .catch((error: unknown) => {
        setScanError(shownError('Не удалось выполнить команду проверки', error))
      })
      .finally(() => {
        setScanBusy(false)
      })
  }

  const scanActions: ScanActions = {
    start: (threshold) => runScanCommand(`scan:${resourceId}:${scan?.id ?? 'none'}:${threshold}`, (operationId) => startScan({ resourceId, threshold, operationId }).unwrap()),
    stop: () => runScanCommand(`stop:${resourceId}:${scan?.id ?? 'none'}`, (operationId) => stopScan({ resourceId, operationId }).unwrap()),
    resume: () => runScanCommand(`resume:${resourceId}:${scan?.id ?? 'none'}`, (operationId) => resumeScan({ resourceId, operationId }).unwrap()),
    reload: () => void refetchScan(),
  }

  const scanVm = buildScanVm({
    result: { ...(scanResult.data !== undefined ? { data: scanResult.data } : {}), ...(scanResult.error !== undefined ? { error: scanResult.error } : {}), isUninitialized: scanResult.isUninitialized, isLoading: scanResult.isFetching, isError: scanResult.isError, isSuccess: scanResult.isSuccess },
    busy: scanBusy,
    commandError: scanError,
  })
  const listVm = buildDuplicatesVm({
    resourceId,
    filter,
    page,
    result: {
      ...(pairsData !== undefined ? { data: pairsData } : {}),
      ...(pairsResult.error !== undefined ? { error: pairsResult.error } : {}),
      isUninitialized: pairsResult.isUninitialized,
      isLoading: pairsResult.isFetching,
      isError: pairsResult.isError,
      isSuccess: pairsResult.isSuccess,
    },
    selected,
    busyPairs,
    pairErrors,
    exportView,
  })

  return <DuplicatesScreen scan={{ vm: scanVm, actions: scanActions }} list={{ vm: listVm, actions: listActions }} />
}
